/**
 * debounce - Delays the execution of a function until a specified time has passed
 * since the last time it was called.
 * Useful for optimizing performance-heavy operations like input filtering or resize events.
 *
 * @param {Function} fn - The function to debounce.
 * @param {number} wait - Delay in milliseconds before calling the function.
 * @returns {Function} - A new debounced function.
 */
function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * wlsTriggerEvent - Dispatches a CustomEvent that bubbles up through the DOM.
 * Use this when parent components or other containers need to listen to the event.
 *
 * @param {Element} element - The DOM element to dispatch the event from.
 * @param {string} name - The name/type of the event.
 * @param {object} data - Optional data payload available under `event.detail`.
 */
function wlsTriggerEvent(element, name, data = {}) {
  element.dispatchEvent(new CustomEvent(name, {
    bubbles: true,
    detail: data
  }));
}

/**
 * wlsTriggerNonBubblingEvent - Dispatches a CustomEvent that does not bubble.
 * Use this when the event should stay localized to the given element only.
 *
 * @param {Element} element - The DOM element to dispatch the event from.
 * @param {string} name - The name/type of the event.
 * @param {object} data - Optional data payload available under `event.detail`.
 */
function wlsTriggerNonBubblingEvent(element, name, data = {}) {
  element.dispatchEvent(new CustomEvent(name, {
    bubbles: false,
    detail: data
  }));
}

/**
 * getGuid - Generates a unique identifier.
 *
 * @returns {string} - A unique identifier.
 */
function getGuid() {
  let d = new Date().getTime();
  let d2 = (performance && performance.now && performance.now() * 1000) || 0;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let r = Math.random() * 16;
    if (d > 0) {
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c == 'x' ? r : (r & 0x7) | 0x8).toString(16);
  });
}
